import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, rem, SCREEN_BOTTOM_PADDING, SettingsItem, withAnimatedNavigationHeader, } from 'design-system';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useEnsureData from 'hooks/useEnsureData';
import InfoCard from 'design-system/InfoCard';
import Text from 'design-system/Text';
import ListEmptyView from 'design-system/ListEmptyView';
import { CollapsableSettingsSection } from 'design-system/CollapsableSettingsSection';
import { StyleSheet, View } from 'react-native';
import { formatDateForItem } from 'utils';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppSelector } from 'store/hooks';
import { selectCreditReportSearches, selectHasCreditReport, } from '../selectors';
import useGetCreditReport from '../hooks/useGetCreditReport';
import { isWeb } from '../../../constants';
import CreditReportLenderLogo from '../components/CreditReportLenderLogo';
const strings = {
    hard: {
        title: 'Hard searches',
        subtitle: 'A hard search happens when you apply for credit. Too many hard searches over a short period of time may impact your credit score and ability to easily obtain credit in the future',
        tooltip: 'Hard searches are removed from your credit report after 2 years',
    },
    soft: {
        title: 'Soft searches',
        subtitle: 'Soft searches have no impact on your credit score, and lenders cannot use them to make lending decisions. They are typically used to check your identity and eligibility',
        tooltip: 'Soft searches are removed from your credit report after 2 years',
    },
};
const keyExtractor = ([company]) => company;
const SearchGroupSection = React.memo(({ company, items, onPressSearch }) => (<CollapsableSettingsSection title={company} description={`${items.length} ${items.length === 1 ? 'search' : 'searches'}`} renderIcon={() => (<CreditReportLenderLogo name={company} inSettingsItem iconUrl={items[0]?.iconUrl}/>)} collapsed enabled={!isWeb} renderChildrenCollapsed={false}>
    {items.map((search, idx) => (<SettingsItem key={`${company}-${search.searchReference ?? idx}`} title={search.searchPurpose ?? 'Search'} description={search.searchDate ? formatDateForItem(search.searchDate) : undefined} onPress={() => onPressSearch(search)}/>))}
  </CollapsableSettingsSection>));
const CreditReportSearchScreen = withAnimatedNavigationHeader(({ scrollHandler, navigation, route }) => {
    const { isSoft } = route.params;
    const fetchCreditReport = useGetCreditReport();
    useEnsureData(selectHasCreditReport, fetchCreditReport);
    const searches = useAppSelector((state) => selectCreditReportSearches(state, isSoft));
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, [navigation]);
    const groups = useMemo(() => {
        const list = searches ?? [];
        const grouped = list.reduce((acc, search) => {
            const key = search.company ?? 'Unknown company';
            acc[key] = acc[key] || [];
            acc[key].push(search);
            return acc;
        }, {});
        return Object.entries(grouped);
    }, [searches]);
    const onPressSearch = useCallback((search) => {
        navigation.navigate('CreditReportSearchDetailScreen', {
            searchReference: search.searchReference ?? undefined,
        });
    }, [navigation]);
    const renderItem = useCallback(({ item: [company, items] }) => (<SearchGroupSection company={company} items={items} onPressSearch={onPressSearch}/>), [onPressSearch]);
    const ListHeader = useCallback(() => (<View style={styles.subtitleContainer}>
            <Text TextThin-16 Gray>
              {strings[isSoft ? 'soft' : 'hard'].subtitle}
            </Text>
          </View>), [isSoft]);
    const ListFooter = useCallback(() => (<InfoCard style={styles.infoCardMargin}>
            <Text TextThin-14 Gray>
              {strings[isSoft ? 'soft' : 'hard'].tooltip}
            </Text>
          </InfoCard>), [isSoft]);
    const ListEmpty = useCallback(() => (<ListEmptyView happy onCard title={`No ${isSoft ? 'soft ' : 'hard '}searches`} style={styles.listEmptyView}/>), [isSoft]);
    const hasGroups = groups.length > 0;
    return (<AppView withFrame={false}>
          <Animated.FlatList data={groups} keyExtractor={keyExtractor} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} showsVerticalScrollIndicator contentContainerStyle={contentContainerStyle} ListHeaderComponent={hasGroups ? ListHeader : null} ListFooterComponent={hasGroups ? ListFooter : null} ListEmptyComponent={ListEmpty}/>
        </AppView>);
}, ({ colors, route }) => ({
    title: strings[route.params.isSoft ? 'soft' : 'hard'].title,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default CreditReportSearchScreen;
const styles = StyleSheet.create({
    subtitleContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(8),
    },
    infoCardMargin: {
        marginTop: rem(16),
    },
    listEmptyView: {
        marginTop: rem(24),
    },
});
